import { inject, Injectable } from '@angular/core';
import { catchError, finalize, Observable, of, Subject, BehaviorSubject, switchMap, tap, map } from 'rxjs';
import { ParameterService } from '../parameter/parameter.service';
import { SvcFeatureToggle } from './svc-feature-toggle.model';
import { SvcCopilotService } from '../svc-copilot.service';
import { SvcParameters } from '../../enums/svc-parameter.enum';

export enum SvcFeatureToggleStatus {
  PENDING,
  LOADED,
}

@Injectable({
  providedIn: 'root',
})
export class SvcFeatureToggleService {
  #parameterService = inject(ParameterService);
  #copilotService = inject(SvcCopilotService);
  #featureToggle = new SvcFeatureToggle();
  #pendingRequest: Subject<SvcFeatureToggleService>;

  #featureToggleStatus$ = new BehaviorSubject(SvcFeatureToggleStatus.PENDING);
  public featureToggleStatus$ = this.#featureToggleStatus$.asObservable();

  public get feedIsEnabled(): boolean {
    return this.#featureToggle.feedIsEnabled.value;
  }
  public get feedIsEnabledAlreadyLoaded(): boolean {
    return this.#featureToggle.feedIsEnabled.hasHave;
  }
  #setFeedIsEnabled(value: boolean): void {
    this.#featureToggle.feedIsEnabled.setValue(value);
  }

  public get postIsEnabled(): boolean {
    return this.#featureToggle.postIsEnabled.value;
  }
  public get postIsEnabledAlreadyLoaded(): boolean {
    return this.#featureToggle.postIsEnabled.hasHave;
  }
  #setPostIsEnabled(value: boolean): void {
    this.#featureToggle.postIsEnabled.setValue(value);
  }

  public get communicationIsEnabled(): boolean {
    return this.#featureToggle.communicationIsEnabled.value;
  }
  public get communicationIsEnabledAlreadyLoaded(): boolean {
    return this.#featureToggle.communicationIsEnabled.hasHave;
  }
  #setCommunicationIsEnabled(value: boolean): void {
    this.#featureToggle.communicationIsEnabled.setValue(value);
  }

  public get appOffilineEnabled(): boolean {
    return this.#featureToggle.appOffilineEnabled.value;
  }
  public get appOffilineEnabledAlreadyLoaded(): boolean {
    return this.#featureToggle.communicationIsEnabled.hasHave;
  }
  #setAppOffilineEnabled(value: boolean): void {
    this.#featureToggle.appOffilineEnabled.setValue(value);
  }

  public get copilotIsEnabled(): boolean {
    return this.#featureToggle.copilotIsEnabled.value;
  }
  public get copilotIsEnabledAlreadyLoaded(): boolean {
    return this.#featureToggle.communicationIsEnabled.hasHave;
  }
  #setCopilotIsEnabled(value: boolean): void {
    this.#featureToggle.copilotIsEnabled.setValue(value);
  }

  public get outlookIntegrationIsEnabled(): boolean {
    return this.#featureToggle.outlookIntegrationIsEnabled.value;
  }
  public get outlookIntegrationIsEnabledAlreadyLoaded(): boolean {
    return this.#featureToggle.outlookIntegrationIsEnabled.hasHave;
  }
  #setOutlookIntegrationIsEnabled(value: boolean): void {
    this.#featureToggle.outlookIntegrationIsEnabled.setValue(value);
  }

  public get checkinIsEnabled(): boolean {
    return this.#featureToggle.checkinIsEnabled.value;
  }
  public get checkinIsEnabledAlreadyLoaded(): boolean {
    return this.#featureToggle.checkinIsEnabled.hasHave;
  }
  #setCheckinIsEnabled(value: boolean): void {
    this.#featureToggle.checkinIsEnabled.setValue(value);
  }

  public reset(): void {
    this.#pendingRequest = null;
    this.#featureToggle = new SvcFeatureToggle();
  }

  public load(): Observable<SvcFeatureToggleService> {
    if (!this.#pendingRequest) {
      const complete = (): void => {
        this.#featureToggleStatus$.next(SvcFeatureToggleStatus.LOADED);
        this.#pendingRequest.next(this);
        this.#pendingRequest.complete();
        this.#pendingRequest.closed = true;
      };
      this.#pendingRequest = new Subject<SvcFeatureToggleService>();
      let pendingCount = 3;
      const tryComplete = (): void => { if (--pendingCount === 0) complete(); };
      this.#parameterService
        .getAllAppGlobalParams()
        .pipe(
          catchError(() => of([])),
          switchMap((res) => {
            const feedIsEnabled = (res.find((p) => p.key === 'FEED_ENABLE')?.value ?? 'FALSE').toString().toLocaleLowerCase();
            const postIsEnabled = (res.find((p) => p.key === 'POST_ENABLE')?.value ?? 'FALSE').toString().toLocaleLowerCase();
            const communicationIsEnabled = (res.find((p) => p.key === 'COMMUNICATION_ENABLE')?.value ?? 'FALSE').toString().toLocaleLowerCase();
            const appOffilineIsEnabled = (res.find((p) => p.key === 'APP_OFFLINE_ENABLE')?.value ?? 'FALSE').toString().toLocaleLowerCase();
            const outlookIntegrationIsEnabled = (res.find((p) => p.key === 'OUTLOOK_INTEGRATION_ENABLE')?.value ?? 'FALSE').toString().toLocaleLowerCase();
            this.#setFeedIsEnabled(typeof feedIsEnabled === 'boolean' ? feedIsEnabled : feedIsEnabled === 'true');
            this.#setPostIsEnabled(typeof postIsEnabled === 'boolean' ? postIsEnabled : postIsEnabled === 'true');
            this.#setCommunicationIsEnabled(typeof communicationIsEnabled === 'boolean' ? communicationIsEnabled : communicationIsEnabled === 'true');
            this.#setAppOffilineEnabled(typeof appOffilineIsEnabled === 'boolean' ? appOffilineIsEnabled : appOffilineIsEnabled === 'true');
            this.#setOutlookIntegrationIsEnabled(outlookIntegrationIsEnabled === 'true');
            return of(this);
          }),
          finalize(() => tryComplete())
        )
        .subscribe();

      this.#copilotService
        .getUserAccessControl()
        .pipe(
          catchError(() => of(false)),
          switchMap((copilotIsEnabled) => {
            this.#setCopilotIsEnabled(copilotIsEnabled as boolean);
            return of(this);
          }),
          finalize(() => tryComplete())
        )
        .subscribe();

      // TODO: checkinEnabled feature when the parameter is ready, for now we set it to true to show the feature in UI
      this.#parameterService
        .getSiteParam(SvcParameters.CheckinEnabled)
        .pipe(
          // catchError(() => of('FALSE')),
          catchError(() => of('TRUE')),
          switchMap((value) => {
            // const checkinIsEnabled = (value ?? 'FALSE').toString().toLocaleLowerCase();
            const checkinIsEnabled = (value ?? 'TRUE').toString().toLocaleLowerCase();
            this.#setCheckinIsEnabled(checkinIsEnabled === 'true');
            return of(this);
          }),
          finalize(() => tryComplete())
        )
        .subscribe();
    }
    return this.#pendingRequest.closed ? of(this) : this.#pendingRequest.asObservable();
  }
}
